/**
 * A tiny "new order" chime via the Web Audio API — no bundled audio asset. Browsers block audio
 * until a user gesture, so `unlockAudio()` is called from the sign-in click; later `playNewOrderChime()`
 * calls (fired by the SSE stream) are then allowed. All calls are no-ops if audio isn't available.
 */

let ctx: AudioContext | null = null

function getCtx(): AudioContext | null {
  if (typeof window === 'undefined') {
    return null
  }
  if (!ctx) {
    const Ctor =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
    if (!Ctor) {
      return null
    }
    ctx = new Ctor()
  }
  return ctx
}

/** Call from a user gesture (the sign-in click) so later programmatic chimes are permitted. */
export function unlockAudio(): void {
  const c = getCtx()
  if (c && c.state === 'suspended') {
    void c.resume()
  }
}

/** A short two-note chime announcing a new order. No-op if audio is unavailable/blocked. */
export function playNewOrderChime(): void {
  const c = getCtx()
  if (!c) {
    return
  }
  if (c.state === 'suspended') {
    void c.resume()
  }
  const now = c.currentTime
  tone(c, 880, now, 0.18) // A5
  tone(c, 1318.5, now + 0.17, 0.22) // E6
}

function tone(c: AudioContext, freq: number, start: number, duration: number): void {
  const osc = c.createOscillator()
  const gain = c.createGain()
  osc.type = 'sine'
  osc.frequency.value = freq
  // Soft attack + exponential decay so it reads as a chime, not a click.
  gain.gain.setValueAtTime(0.0001, start)
  gain.gain.exponentialRampToValueAtTime(0.3, start + 0.02)
  gain.gain.exponentialRampToValueAtTime(0.0001, start + duration)
  osc.connect(gain).connect(c.destination)
  osc.start(start)
  osc.stop(start + duration + 0.02)
}
